"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { 
  Cpu, 
  ArrowRight, 
  CheckCircle2, 
  ShieldCheck, 
  Users,
  Lock,
  LineChart,
  Network,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PipelineSimulator } from "@/components/sections/saas/PipelineSimulator";
import { TalentCalculator } from "@/components/sections/saas/TalentCalculator";

export default function SaasContent() {
  const features = [
    {
      icon: <Users className="h-6 w-6 text-primary" />,
      title: "Automated Candidate Sourcing",
      description: "Direct integration with premium engineering databases and networks to pool matching profiles within minutes."
    },
    {
      icon: <Cpu className="h-6 w-6 text-accent" />,
      title: "AI Vetting & Screening",
      description: "AI conducts automated introductory calls and screens candidates on tech stack proficiency and budget fits."
    },
    {
      icon: <ShieldCheck className="h-6 w-6 text-success" />,
      title: "PF/EPFO Verification Sync",
      description: "Instantly fetch employment history logs to weed out fake experience profiles and verify statutory compliances."
    },
    {
      icon: <Lock className="h-6 w-6 text-primary" />,
      title: "SOC 2 Type II Compliance",
      description: "Strict tenant data isolation and end-to-end encryption protocols protecting sensitive candidate identity assets."
    },
    {
      icon: <LineChart className="h-6 w-6 text-accent" />,
      title: "Dynamic Match Index",
      description: "Matches are ranked dynamically using deep learning models against your specific tech stack constraints."
    },
    {
      icon: <Network className="h-6 w-6 text-success" />,
      title: "Applicant Tracking API Hub",
      description: "Connect candidate logs directly into your existing enterprise ATS (Workday, Greenhouse, or Lever) in seconds."
    }
  ];

  return (
    <div className="w-full flex-grow py-20 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden">
      {/* Background mesh glow */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="bg-mesh" />
      </div>

      <div className="mx-auto max-w-7xl relative z-10 space-y-28">
        
        {/* 1. Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 flex flex-col gap-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 dark:bg-primary/10 border border-primary/10 text-primary dark:text-accent text-xs font-bold w-fit">
              <Sparkles className="h-4 w-4 text-accent animate-pulse" />
              TalentFlow AI — By Johnson Softwares
            </div>
            
            <h1 className="text-4xl sm:text-6xl font-heading font-extrabold text-foreground tracking-tight leading-tight">
              Enterprise hiring, <span className="premium-gradient">automated by AI.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-text-muted leading-relaxed">
              Slash your average sourcing cycle from 30 days to 48 hours. TalentFlow AI automates outbound matching, phone vetting, and PF/EPFO background compliance checks in one unified hub.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 mt-2">
              <Button href="/contact" variant="gradient" size="lg" className="w-full sm:w-auto" magnetic>
                Start 7-Day Free Trial
              </Button>
              <Button href="#demo" variant="outline" size="lg" className="w-full sm:w-auto flex items-center justify-center gap-2">
                See Live Demo
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-6 mt-4 text-xs text-text-muted font-medium">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-success" />
                No credit card required
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-success" />
                SOC 2 Compliant
              </span>
            </div>
          </motion.div>

          {/* Hero Mockup Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <div className="relative p-2 bg-slate-100 dark:bg-slate-900 border border-border-muted/50 rounded-2xl shadow-2xl">
              <PipelineSimulator />
            </div>
          </motion.div>
        </div>

        {/* 2. Client Logos Marquee */}
        <div className="text-center py-6 border-y border-border-muted/30">
          <p className="text-xs uppercase font-bold text-text-muted tracking-widest mb-6">
            TRUSTED BY THE WORLD&apos;S BEST TEAMS
          </p>
          <div className="flex flex-wrap justify-center gap-10 opacity-70">
            {["Vercel", "Stripe", "Linear", "Notion", "AWS", "Google Cloud"].map((logo) => (
              <span key={logo} className="text-sm font-heading font-extrabold text-foreground/80 tracking-wider">
                {logo}
              </span>
            ))}
          </div>
        </div>

        {/* 3. Pipeline Simulator Spotlight */}
        <div id="demo" className="space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs uppercase font-heading tracking-widest text-primary font-bold">
              Funnel Automation
            </span>
            <h2 className="text-3xl sm:text-5xl font-heading font-extrabold text-foreground mt-3 leading-tight">
              Watch candidate flow in real time.
            </h2>
            <p className="text-base sm:text-lg text-text-muted mt-4">
              TalentFlow AI coordinates outreach, answers questions, scores tech proficiency, and logs verified details directly to your dashboard.
            </p>
          </div>

          <PipelineSimulator />
        </div>

        {/* 4. Bento Feature Grid */}
        <div className="space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs uppercase font-heading tracking-widest text-accent font-bold">
              System Capabilities
            </span>
            <h2 className="text-3xl sm:text-5xl font-heading font-extrabold text-foreground mt-3 leading-tight">
              SaaS infrastructure engineered for scale.
            </h2>
            <p className="text-base sm:text-lg text-text-muted mt-4">
              Built on multi-tenant isolation, verified statutory APIs, and low-latency matching models.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feat, idx) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <Card className="h-full flex flex-col justify-between" glowColor="rgba(6, 182, 212, 0.1)">
                  <div>
                    <div className="p-3 bg-primary/10 rounded-lg w-fit mb-6 text-primary dark:bg-primary/20 dark:text-accent">
                      {feat.icon}
                    </div>
                    <h3 className="font-heading font-extrabold text-xl text-foreground mb-3">
                      {feat.title}
                    </h3>
                    <p className="text-xs text-text-muted leading-relaxed">
                      {feat.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 5. ROI Savings Calculator Section */}
        <div className="space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs uppercase font-heading tracking-widest text-primary font-bold">
              Measurable Business Value
            </span>
            <h2 className="text-3xl sm:text-5xl font-heading font-extrabold text-foreground mt-3 leading-tight">
              Sourcing fees shouldn&apos;t drain budgets.
            </h2>
            <p className="text-base sm:text-lg text-text-muted mt-4">
              Stop paying 20% commission per position. Transition to flat-rate automation and save thousands.
            </p>
          </div>

          <TalentCalculator />
        </div>

        {/* 6. Enterprise Objections FAQ Accordion */}
        <div className="space-y-12 max-w-4xl mx-auto">
          <div className="text-center">
            <span className="text-xs uppercase font-heading tracking-widest text-accent font-bold">
              Objection Handling
            </span>
            <h2 className="text-2xl sm:text-4xl font-heading font-extrabold text-foreground mt-3">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            {[
              {
                q: "How does the EPFO / PF verification sync work?",
                a: "TalentFlow AI integrates directly with authorized statutory APIs. With candidate consent, it fetches historical employee contribution records from EPFO and ESIC databases to cross-verify exact employment timelines and job titles instantly."
              },
              {
                q: "Can we integrate TalentFlow with our existing ATS?",
                a: "Yes! We offer pre-built API sync connections for Workday, Greenhouse, Lever, and Zoho Recruit. Once a candidate is marked as Vetted, their profile is automatically pushed into your target dashboard."
              },
              {
                q: "How secure is the tenant data isolation model?",
                a: "Every customer operates on a completely isolated database schema inside AWS. All candidate identity logs, resumes, and video transcripts are fully encrypted at rest (AES-256) and in transit (TLS 1.3)."
              },
              {
                q: "What are the monthly subscription options?",
                a: "Our flat-rate plan is ₹12,000 per month (billed annually at ₹1,44,000), which covers unlimited candidate sourcing matching and verification up to 50 active roles. Custom enterprise billing is available for larger setups."
              }
            ].map((faq) => (
              <div
                key={faq.q}
                className="w-full bg-white dark:bg-slate-900 rounded-xl border border-border-muted/50 overflow-hidden shadow-sm"
              >
                <details className="group [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex items-center justify-between p-6 cursor-pointer focus:outline-none focus:bg-slate-50 dark:focus:bg-slate-800">
                    <h3 className="font-heading font-semibold text-base sm:text-lg text-foreground flex items-center gap-3">
                      <HelpCircle className="h-5 w-5 text-primary shrink-0 dark:text-accent" />
                      {faq.q}
                    </h3>
                    <span className="ml-4 shrink-0 transition duration-300 group-open:-rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="h-5 w-5 text-text-muted"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6 pt-2 border-t border-border-muted/30">
                    <p className="text-xs text-text-muted leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* 7. Bottom CTA Banner */}
        <div className="relative py-16 text-center">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/5 rounded-3xl blur-2xl pointer-events-none" />
          
          <div className="relative z-10 p-8 sm:p-16 rounded-3xl bg-slate-900 text-white dark:bg-slate-950 border border-slate-800 shadow-2xl flex flex-col items-center gap-6">
            <span className="text-xs uppercase font-heading tracking-widest text-accent font-bold">
              Ready to automate?
            </span>
            <h2 className="text-3xl sm:text-5xl font-heading font-extrabold max-w-2xl leading-tight">
              Start sourcing vetted talent in hours.
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl">
              Get full access to the matching engine, compliance check modules, and dynamic ATS integrations free for 7 days. Sign up in under 2 minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 mt-4 w-full sm:w-auto">
              <Button href="/contact" variant="gradient" size="lg" className="w-full sm:w-auto" magnetic>
                Start Free Trial
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="w-full sm:w-auto border-slate-700 hover:bg-slate-800 text-white">
                Contact Enterprise Sales
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// Sparkles local mini-icon
const Sparkles = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z" />
    <path d="m5 3 1 2.5L8.5 6 6 7 5 9.5 4 7 1.5 6 4 5.5Z" />
    <path d="m19 17 1 2.5 2.5.5-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1Z" />
  </svg>
);
